/**
 * AppShell — Sprint 6.1 (UX/UI Refactor NOC).
 *
 * Container do novo layout NOC: sidebar recolhível à esquerda,
 * topbar fino no topo, busca global (Ctrl/Cmd+K), e a área de
 * conteúdo onde as páginas existentes são renderizadas via children.
 *
 * O App.jsx continua dono do roteamento de views — o AppShell só
 * fornece o "chrome" em volta. Nenhuma página foi alterada.
 *
 * Estado persistido em memória de sessão (useState) — sidebar
 * recolhida e favoritos. Não usa localStorage (restrição de
 * artifacts); num deploy real, dá para promover a localStorage.
 */
import { useEffect, useState } from "react";
import Sidebar from "./Sidebar.jsx";
import ShellTopbar from "./ShellTopbar.jsx";
import GlobalSearch from "./GlobalSearch.jsx";
import { ALL_NAV_ITEMS } from "../../nav/navConfig.js";
import { t } from "../../i18n/index.js";

export default function AppShell({
  currentView,
  setView,
  backendOk,
  lastRefresh,
  refreshing,
  onRefresh,
  alertsOpen = 0,
  session = null,
  onLogout,
  children,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Favoritos padrão — as views mais usadas no dia a dia do NOC.
  const [favorites, setFavorites] = useState([
    "dashboard", "fleet", "alerts",
  ]);

  // Atalho global Ctrl/Cmd+K abre a busca.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function toggleFavorite(view) {
    setFavorites((prev) =>
      prev.includes(view)
        ? prev.filter((v) => v !== view)
        : [...prev, view]
    );
  }

  // Título da view atual para o topbar.
  const currentItem = ALL_NAV_ITEMS.find((it) => it.view === currentView);
  const viewTitle = currentItem
    ? t(currentItem.labelKey)
    : t("nav.dashboard");

  return (
    <div className="ipwd-shell min-h-screen flex">
      <Sidebar
        currentView={currentView}
        setView={setView}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        alertsOpen={alertsOpen}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      {/* Spacer — reserva os 72px da sidebar recolhida no fluxo.
          Quando a sidebar flutua no hover (vira lg:fixed), ela sai
          do fluxo; este spacer mantém o conteúdo no lugar para não
          "pular". Só atua em lg+ e quando collapsed. */}
      {collapsed && (
        <div className="hidden lg:block w-[72px] shrink-0"
             aria-hidden="true" />
      )}

      {/* Coluna de conteúdo */}
      <div className="flex-1 min-w-0 flex flex-col">
        <ShellTopbar
          viewTitle={viewTitle}
          backendOk={backendOk}
          lastRefresh={lastRefresh}
          refreshing={refreshing}
          onRefresh={onRefresh}
          onOpenSearch={() => setSearchOpen(true)}
          onOpenMobileMenu={() => setMobileOpen(true)}
          onQuickAction={(view) => setView(view)}
          session={session}
          onLogout={onLogout}
        />

        {/* Área das páginas — children renderizado pelo App.jsx.
            Padding responsivo: px-3 no mobile, px-6/py-4 no
            desktop. O wrapper interno limita a largura para o
            conteúdo não esticar demais em telas muito largas. */}
        <main className="flex-1 min-w-0 overflow-x-hidden
                         px-3 py-4 sm:px-6">
          <div className="mx-auto w-full max-w-[1600px]">
            {children}
          </div>
        </main>
      </div>

      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(view) => setView(view)}
      />
    </div>
  );
}
